import { Clear, Close, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cell,
  Tooltip as ChartTooltip,
  LabelList,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import { setResetBool } from '../../../../stores/slices/graphSlice';
import { setYAxisType, setYAxisTypeTrend } from '../../../../stores/slices/mapSlice';
import { darkGrey, hoverColor } from '../../../../util/colorHelper';
import { drugClassesRulesNG, drugClassesRulesSTHeatMap, statKeys } from '../../../../util/drugClassesRules';
import {
  drugAcronyms,
  drugAcronymsOpposite,
  drugsSA,
  drugsSP,
  markersDrugsSH,
  markersDrugsKP,
} from '../../../../util/drugs';
import { longestVisualWidth } from '../../../../util/helpers';
import { isTouchDevice } from '../../../../util/isTouchDevice';
import { organismsCards, organismsWithLotsGenotypes } from '../../../../util/organismsCards';
import { heatmapLegendGradient, heatmapTextColor, mixColorScale } from '../../Map/mapColorHelper';
import { useStyles } from './BubbleGeographicGraphMUI';
import { PlottingOptionsPanel } from '../../Shared/PlottingOptionsPanel';
import { getLocalizedCountryName } from '../../../../util/countryLocalization';

// Dynamic trend options generator
const createTrendOptions = (organism, drugs, labelMap = {}) => {
  return drugs
    .map(drug => {
      const label = labelMap[drug] || drug;
      return {
        organism,
        key: drug,
        value: `${organism}-trends-${drug.toLowerCase()}`,
        label: label,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
};

// ecoli-family organisms: the 'Resistance marker' view collapses
// 'Ciprofloxacin NS'/'Ciprofloxacin R' into a single 'Ciprofloxacin' entry
// (≥1 marker in Quinolone). The 'Resistance prevalence' view keeps them split
// because it reads `resistanceOptions` (statKeys filtered by resistanceView)
// rather than this organismConfig.drugs list.
const markersDrugsSHSimpleCipro = markersDrugsSH.filter(
  d => d !== 'Ciprofloxacin NS' && d !== 'Ciprofloxacin R',
);

const organismConfig = {
  kpneumo: {
    drugs: markersDrugsKP,
    labelMap: { ESBL: 'ESBLs' },
  },
  styphi: {
    drugs: Object.keys(drugClassesRulesSTHeatMap),
    labelMap: {},
  },
  ngono: {
    drugs: Object.keys(drugClassesRulesNG),
    labelMap: {},
  },
  shige: {
    drugs: markersDrugsSHSimpleCipro,
    labelMap: {},
  },
  ecoli: {
    drugs: markersDrugsSHSimpleCipro,
    labelMap: {},
  },
  decoli: {
    drugs: markersDrugsSHSimpleCipro,
    labelMap: {},
  },
  senterica: {
    drugs: markersDrugsSHSimpleCipro,
    labelMap: {},
  },
  sentericaints: {
    drugs: markersDrugsSHSimpleCipro,
    labelMap: {},
  },
  saureus: {
    drugs: drugsSA,
    labelMap: {},
  },
  strepneumo: {
    drugs: drugsSP,
    labelMap: {},
  },
};

// function to get trend options for any organism
const getAllTrendOptionsForOrganism = organism => {
  const config = organismConfig[organism];

  if (!config) {
    console.warn(`No trend options configuration found for organism: ${organism}`);
    return [];
  }

  return createTrendOptions(organism, config.drugs, config.labelMap);
};

// Generate all trend options
const kpTrendOptions = getAllTrendOptionsForOrganism('kpneumo');
const stTrendOptions = getAllTrendOptionsForOrganism('styphi');
const ngonoTrendOptions = getAllTrendOptionsForOrganism('ngono');
const shigeTrendOptions = getAllTrendOptionsForOrganism('shige');
const decoliTrendOptions = getAllTrendOptionsForOrganism('decoli');
const ecoliTrendOptions = getAllTrendOptionsForOrganism('ecoli');
const sentericaTrendOptions = getAllTrendOptionsForOrganism('senterica');
const sentericaintsTrendOptions = getAllTrendOptionsForOrganism('sentericaints');
const saureusTrendOptions = getAllTrendOptionsForOrganism('saureus');
const strepneumoTrendOptions = getAllTrendOptionsForOrganism('strepneumo');

const trendOptionsMap = {
  kpneumo: kpTrendOptions,
  styphi: stTrendOptions,
  ngono: ngonoTrendOptions,
  shige: shigeTrendOptions,
  decoli: decoliTrendOptions,
  ecoli: ecoliTrendOptions,
  senterica: sentericaTrendOptions,
  sentericaints: sentericaintsTrendOptions,
  saureus: saureusTrendOptions,
  strepneumo: strepneumoTrendOptions,
};

// labelKey entries are translated at render time via t(); `label` kept as
// the English fallback used for sorting and for older code paths.
const yOptions = [
  {
    value: 'genotype',
    label: 'Genotype prevalence',
    labelKey: 'dashboard.mapViews.genotypePrevalence',
    // shige shows 'ST (7-locus MLST) prevalence' + the two LINcode dimensions instead.
    organisms: organismsCards.map(x => x.value).filter(x => !['sentericaints', 'senterica', 'shige'].includes(x)),
  },
  {
    value: 'genotype',
    label: 'Lineage prevalence (ST)',
    labelKey: 'dashboard.mapViews.lineagePrevalence',
    organisms: ['sentericaints', 'senterica'],
  },
  {
    value: 'genotype',
    label: 'ST (7-locus MLST) prevalence',
    organisms: ['shige'],
  },
  {
    // genotype mapped from the LINcode (not the LINcode itself), shown as
    // 'Genotype prevalence' per review. The named alias column was removed.
    value: 'lincode',
    label: 'Genotype prevalence',
    labelKey: 'dashboard.mapViews.genotypePrevalence',
    organisms: ['shige'],
  },
  {
    value: 'resistance',
    label: 'Resistance prevalence',
    labelKey: 'dashboard.mapViews.resistancePrevalence',
  },
  {
    value: 'determinant',
    label: 'Resistance marker',
    labelKey: 'common.resistanceMarker',
    organisms: ['styphi', 'kpneumo', 'ngono', 'shige', 'ecoli', 'decoli', 'senterica', 'sentericaints', 'saureus', 'strepneumo'],
  },
  {
    value: 'pathotype',
    label: 'Pathotype prevalence',
    labelKey: 'dashboard.mapViews.pathotypePrevalence',
    organisms: ['shige', 'decoli'],
  },
  {
    value: 'serotype',
    label: 'Serotype prevalence',
    labelKey: 'dashboard.mapViews.serotypePrevalence',
    organisms: ['sentericaints', 'senterica'],
  },
  {
    value: 'ngmast',
    label: 'NG-MAST prevalence',
    labelKey: 'dashboard.mapViews.ngMastPrevalence',
    organisms: ['ngono'],
  },
].sort((a, b) => a.label.localeCompare(b.label));

export const BubbleGeographicGraph = ({ showFilter, setShowFilter, filterButtonRef }) => {
  const classes = useStyles();
  const [xAxisType, setXAxisType] = useState('country');
  const [xAxisSelected, setXAxisSelected] = useState([]);
  const [yAxisSelected, setYAxisSelected] = useState([]);
  const [genotypeSearch, setGenotypeSearch] = useState('');
  const [plotChart, setPlotChart] = useState(null);
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();
  const organism = useAppSelector(state => state.dashboard.organism);
  const canGetData = useAppSelector(state => state.dashboard.canGetData);
  const mapData = useAppSelector(state => state.map.mapData);
  const mapRegionData = useAppSelector(state => state.map.mapRegionData);
  const economicRegions = useAppSelector(state => state.dashboard.economicRegions);
  const drugsRegionsData = useAppSelector(state => state.graph.drugsRegionsData);
  const drugsCountriesData = useAppSelector(state => state.graph.drugsCountriesData);
  const genotypesDrugClassesData = useAppSelector(state => state.graph.genotypesDrugClassesData);
  const yAxisType = useAppSelector(state => state.map.yAxisType);
  const yAxisTypeTrend = useAppSelector(state => state.map.yAxisTypeTrend);
  const loadingPDF = useAppSelector(state => state.dashboard.loadingPDF);
  const resetBool = useAppSelector(state => state.graph.resetBool);

  useEffect(() => {
    setXAxisType('country');
    dispatch(setYAxisType('resistance'));
    dispatch(setResetBool(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organism, resetBool]);
  const organismHasLotsOfGenotypes = useMemo(() => organismsWithLotsGenotypes.includes(organism), [organism]);

  const drugsData = useMemo(() => {
    return xAxisType === 'country' ? drugsCountriesData : drugsRegionsData;
  }, [drugsCountriesData, drugsRegionsData, xAxisType]);

  const resistanceOptions = useMemo(() => {
    const options = statKeys[organism] ? statKeys[organism] : statKeys['others'];
    const resistance = options.filter(option => option.resistanceView).map(option => option.name);

    // if (organism === 'ngono') {
    //   resistance.splice(5, 0, ...['XDR', 'MDR']);
    // }

    const drugs = {};

    (xAxisType === 'country' ? mapData : mapRegionData)
      .filter(item => xAxisSelected.includes(item.name))
      .forEach(item => {
        const stats = item.stats;
        if (!stats) return;

        resistance.forEach(drug => {
          if (!(drug in drugs)) {
            drugs[drug] = stats[drug]?.count ?? 0;
            return;
          }

          drugs[drug] += stats[drug]?.count ?? 0;
        });
      });

    return (
      Object.entries(drugs)
        .filter(x => x[1] > 0 || x[0] === 'Pansusceptible')
        .map(x => x[0]) ?? []
    );
  }, [mapData, mapRegionData, organism, xAxisSelected, xAxisType]);

  const markersOptions = useMemo(() => {
    const organismTrendOptions = getAllTrendOptionsForOrganism(organism);

    const drugKey = organismTrendOptions?.find(x => x.value === yAxisTypeTrend)?.key;

    if (!drugKey) return [];

    // Common exclusions for all organisms (exclude non-determinant placeholders)
    const EXCLUSIONS = new Set(['None', 'name', 'count', 'resistantCount', 'totalCount', '-', 'ND', '']);

    // Helper function to aggregate drug data
    const aggregateDrugs = (dataArray, filterFn = () => true) => {
      const drugs = {};

      dataArray.filter(filterFn).forEach(item => {
        Object.entries(item).forEach(([key, value]) => {
          if (EXCLUSIONS.has(key)) return;
          drugs[key] = (drugs[key] || 0) + (value || 0);
        });
      });

      return Object.entries(drugs)
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
    };

    // Get data source - for geographic comparisons, all organisms use drugsData
    const dataSource = drugsData?.[drugKey];

    if (!dataSource) return [];

    // Apply filtering to only show selected countries/regions
    const filterFn = item => xAxisSelected.includes(item.name);

    return aggregateDrugs(dataSource, filterFn);
  }, [drugsData, xAxisSelected, yAxisTypeTrend, organism]);

  const GLPSColumn = useMemo(() => {
    if (['serotype', 'pathotype'].includes(yAxisType)) {
      return 'PATHOTYPE';
    }
    if (yAxisType === 'ngmast') {
      return 'NGMAST';
    }
    if (yAxisType === 'lincode') {
      return 'LINCODE_NUM';
    }

    return 'GENOTYPE';
  }, [yAxisType]);

  const GLPSEntries = useMemo(() => {
    const filteredData = (xAxisType === 'country' ? mapData : mapRegionData).filter(x =>
      xAxisSelected.includes(x.name),
    );
    const items = {};

    filteredData.forEach(obj => {
      // Guard against missing stats or missing GLPSColumn entries
      obj.stats?.[GLPSColumn]?.items?.forEach(item => {
        if (!(item.name in items)) {
          items[item.name] = item.count;
          return;
        }

        items[item.name] += item.count;
      });
    });

    return items;
  }, [GLPSColumn, mapData, mapRegionData, xAxisSelected, xAxisType]);

  const xAxisOptions = useMemo(() => {
    switch (xAxisType) {
      case 'country':
        return mapData.filter(x => x.count >= 20).sort(); // data to plot should include count greater and equal to 20
      case 'region':
        return mapRegionData.filter(x => x.count >= 20 && x.name !== 'All').sort();
      default:
        return [];
    }
  }, [mapData, mapRegionData, xAxisType]);

  const yAxisOptions = useMemo(() => {
    switch (yAxisType) {
      case 'resistance':
        return resistanceOptions;
      case 'determinant':
        return markersOptions ?? [];
      default:
        return (
          Object.entries(GLPSEntries)
            .sort((a, b) => b[1] - a[1])
            .map(x => x[0]) ?? []
        );
    }
  }, [GLPSEntries, markersOptions, resistanceOptions, yAxisType]);

  const filteredYAxisOptions = useMemo(() => {
    const filteredOptions = yAxisOptions.filter(option => option.toLowerCase().includes(genotypeSearch.toLowerCase()));

    if (
      ['serotype', 'determinant'].includes(yAxisType) ||
      (['genotype', 'ngmast', 'lincode'].includes(yAxisType) && organismHasLotsOfGenotypes)
    ) {
      return filteredOptions.slice(0, 20);
    }

    return filteredOptions;
  }, [yAxisOptions, yAxisType, organismHasLotsOfGenotypes, genotypeSearch]);

  useEffect(() => {
    setXAxisSelected(
      (xAxisType === 'country'
        ? mapData.filter(x => x.count >= 20)
        : mapRegionData.filter(x => x.count >= 20 && x.name !== 'All')
      )
        .slice()
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
        .map(item => item.name),
    );
  }, [mapData, mapRegionData, xAxisType]);

  useEffect(() => {
    setYAxisSelected(
      ['genotype', 'serotype', 'ngmast', 'lincode'].includes(yAxisType) ||
      (yAxisType === 'determinant' && organism === 'kpneumo')
        ? yAxisOptions.slice(0, 10)
        : yAxisOptions,
    );
  }, [yAxisOptions, yAxisType, organism]);

  // Drugs are full names in yAxisSelected; expand 'CipR'/'MDR'/etc. to their
  // human-readable label via drugAcronymsOpposite so the row label on the chart
  // matches what the user picked in the Select-drug dropdown.
  const formatYLabel = useCallback(
    name => (yAxisType === 'resistance' ? drugAcronymsOpposite[drugAcronyms[name] ?? name] ?? name : name),
    [yAxisType],
  );

  const yAxisWidth = useMemo(() => {
    return longestVisualWidth((yAxisSelected ?? []).map(formatYLabel));
  }, [yAxisSelected, formatYLabel]);

  const getOptionLabel = useCallback(
    item => {
      if (!['genotype', 'serotype', 'pathotype', 'ngmast', 'lincode'].includes(yAxisType)) {
        return drugAcronymsOpposite[drugAcronyms[item] ?? item] ?? item;
      }

      return `${item} (total N=${GLPSEntries[item]})`;
    },
    [GLPSEntries, yAxisType],
  );

  const yAxisLabel = useMemo(() => {
    switch (yAxisType) {
      case 'resistance':
        return 'drugs';
      case 'determinant':
        return 'markers';
      case 'ngmast':
        return 'ng-mast';
      case 'lincode':
        return 'genotypes';
      case 'genotype':
        return ['sentericaints', 'senterica'].includes(organism)
          ? 'lineages (ST)'
          : organism === 'shige'
            ? 'STs'
            : 'genotypes';
      default:
        return organism === 'sentericaints' ? 'serotypes' : 'pathotypes';
    }
  }, [organism, yAxisType]);

  function handleChangeXAxisType(event) {
    setXAxisType(event.target.value);
  }

  function handleChangeYAxisType(event) {
    const value = event.target.value;
    dispatch(setYAxisType(value));

    if (value === 'determinant') {
      const organismTrendOptions = getAllTrendOptionsForOrganism(organism);
      if (organismTrendOptions.length > 0) {
        dispatch(setYAxisTypeTrend(organismTrendOptions[0].value));
      }
    }
  }

  function handleChangeYAxisTypeTrend(event) {
    dispatch(setYAxisTypeTrend(event.target.value));
  }

  function handleChangeXAxisSelected({ event = null, all = false }) {
    if (!all) {
      const newValue = event.target.value;
      setXAxisSelected(newValue);
      return;
    }

    if (xAxisSelected.length === xAxisOptions.length) {
      setXAxisSelected([]);
      return;
    }

    setXAxisSelected(xAxisOptions.map(x => x.name));
  }

  // const showSelectAll = useMemo(() => {
  //   return yAxisType !== 'genotype'
  //     ? yAxisSelected?.length !== yAxisOptions?.length
  //     : !organismHasLotsOfGenotypes && yAxisSelected?.length !== yAxisOptions?.length;
  // }, [organismHasLotsOfGenotypes, yAxisOptions?.length, yAxisSelected?.length, yAxisType]);

  function handleChangeYAxisSelected({ event = null, all = false }) {
    const value = event?.target.value;

    if (value?.length === 1 && value[0] === undefined) {
      return;
    }

    if (!all) {
      setYAxisSelected(value);
      return;
    }

    if (
      yAxisSelected.length === filteredYAxisOptions.length ||
      yAxisSelected.some(x => !yAxisOptions.slice(0, 20).includes(x))
    ) {
      setYAxisSelected([]);
      return;
    }

    setYAxisSelected(filteredYAxisOptions?.slice());
  }

  function handleChangeSearch(event) {
    event.stopPropagation();
    setGenotypeSearch(event.target.value);
  }

  function clearSearch(event) {
    event.stopPropagation();
    setGenotypeSearch('');
  }

  // Top axis area reserved for the rotated column labels on the first chart.
  // Allocated via XAxis height so the cell row stays at the bottom of the
  // first chart (immediately adjacent to row 2 below it) instead of being
  // pushed into the middle by extra container padding.
  const FIRST_ROW_AXIS_HEIGHT = 150;

  const getTitle = useCallback(value => {
    return drugAcronymsOpposite[value] ?? Object.keys(drugAcronyms).find(key => drugAcronyms[key] === value) ?? value;
  }, []);

  const countriesTooltipForRegion = useCallback(
    (region, stat, parent) => {
      if (xAxisType === 'country') {
        return null;
      }

      const countries = economicRegions[region];
      const item =
        organism === 'ngono' && stat === 'SUS'
          ? 'Susceptible'
          : (Object.keys(drugAcronyms).find(key => drugAcronyms[key] === stat) ?? stat);
      // const regionCount = mapRegionData.find((x) => x.name === region).count;

      const items = [];
      mapData.forEach(x => {
        const checkCountry = countries.includes(x.name);

        if (!checkCountry) {
          return;
        }

        const info = {
          countryName: x.name,
          countryCount: x.count,
        };

        if (yAxisType === 'resistance') {
          const count = x.stats?.[item]?.count || 0;

          if (count > 0) {
            info['itemCount'] = count;
            info['percentage'] = x.stats?.[item]?.percentage || 0;
            items.push(info);
          }
        }

        if (['genotype', 'serotype', 'pathotype', 'ngmast', 'lincode'].includes(yAxisType)) {
          const gen = x.stats?.[GLPSColumn]?.items?.find(g => g && g.name === item);

          if (gen?.count > 0) {
            info['itemCount'] = gen?.count || 0;
            info['percentage'] = gen ? Number(((gen.count / x.count) * 100).toFixed(2)) : 0;
            items.push(info);
          }
        }

        if (yAxisType === 'determinant') {
          // For all organisms, use drugsCountriesData for geographic comparisons
          const bucketParent = drugsCountriesData?.[parent] || [];
          const count = Array.isArray(bucketParent) ? bucketParent.find(g => g && g.name === x.name)?.[item] || 0 : 0;

          if (count > 0) {
            info['itemCount'] = count;
            info['percentage'] = Number(((count / x.count) * 100).toFixed(2));
            items.push(info);
          }
        }
      });

      items.sort((a, b) => b.itemCount - a.itemCount);

      return (
        <div>
          <br />
          {items.slice(0, 10).map((x, index) => {
            return (
              <Typography fontSize="12px" key={`country-tooltip-${index}`}>
                <Typography component="span" fontWeight={500} fontSize="12px" display="inline">
                  {xAxisType === 'country' ? getLocalizedCountryName(x.countryName, i18n.language) : x.countryName}
                </Typography>
                {`: ${x.itemCount}/${x.countryCount} (${x.percentage}%)`}
              </Typography>
            );
          })}
        </div>
      );
    },
    [GLPSColumn, drugsCountriesData, economicRegions, mapData, organism, xAxisType, yAxisType],
  );

  const configuredMapData = useMemo(() => {
    if (yAxisSelected.length === 0 || xAxisSelected.length === 0) {
      return [];
    }

    const locationItems = (xAxisType === 'country' ? mapData : mapRegionData).filter(
      item => item && xAxisSelected.includes(item.name),
    );

    const displayLocation = name => (name === 'United States of America' ? 'USA' : name);

    if (yAxisType === 'resistance') {
      const drugRows = yAxisSelected
        .filter(drug => resistanceOptions.includes(drug))
        .map(drug => {
          const row = { name: drug, items: [] };
          locationItems.forEach(loc => {
            const value = loc.stats?.[drug];
            row.items.push({
              itemName: displayLocation(loc.name),
              percentage: value?.percentage ?? 0,
              count: value?.count ?? 0,
              index: 1,
              typeName: loc.name,
              total: loc.count,
              drug,
            });
          });
          return row;
        });

      // Preserve previous drug-axis ordering rules on the row axis. Compare by
      // acronym so the styphi/sentericaints rules (CRO/CipR/CipNS first) and
      // the global rules (MDR/XDR/PAN/SUS last) still apply.
      const acronymOf = name => drugAcronyms[name] ?? name;
      drugRows.sort((a, b) => acronymOf(a.name).localeCompare(acronymOf(b.name)));

      const moveToStart = acronym => {
        const i = drugRows.findIndex(x => acronymOf(x.name) === acronym);
        if (i >= 0) drugRows.unshift(...drugRows.splice(i, 1));
      };

      const moveToEnd = acronym => {
        const i = drugRows.findIndex(x => acronymOf(x.name) === acronym);
        if (i >= 0) drugRows.push(...drugRows.splice(i, 1));
      };

      if (['styphi', 'sentericaints'].includes(organism)) {
        ['CRO', 'CipR', 'CipNS'].forEach(moveToStart);
      }
      ['MDR', 'XDR', 'PAN', 'SUS'].forEach(moveToEnd);

      return drugRows;
    }

    if (['genotype', 'serotype', 'pathotype', 'ngmast', 'lincode'].includes(yAxisType)) {
      return yAxisSelected.map(selected => {
        const row = { name: selected, items: [] };
        locationItems.forEach(loc => {
          const gen = loc?.stats?.[GLPSColumn]?.items?.find(g => g && g.name === selected);
          row.items.push({
            itemName: displayLocation(loc.name),
            percentage: gen ? Number(((gen.count / loc.count) * 100).toFixed(2)) : 0,
            index: 1,
            typeName: loc.name,
            count: gen?.count || 0,
            total: loc.count,
            drug: selected,
          });
        });
        return row;
      });
    }

    if (yAxisType === 'determinant') {
      const organismTrendOptions = getAllTrendOptionsForOrganism(organism);
      const drugKey = organismTrendOptions?.find(x => x.value === yAxisTypeTrend)?.key;
      if (!drugKey) return [];

      const calculatePercentage = (count, total) =>
        count && total ? Number(((count / total) * 100).toFixed(2)) : 0;

      return yAxisSelected.map(drug => {
        const row = { name: drug.replaceAll(' + ', '/'), items: [] };
        locationItems.forEach(loc => {
          const locationData = drugsData?.[drugKey]?.find(x => x.name === loc.name);
          const count = locationData?.[drug] || 0;
          const total = locationData?.totalCount || 0;
          row.items.push({
            itemName: displayLocation(loc.name),
            percentage: calculatePercentage(count, total),
            index: 1,
            typeName: loc.name,
            count,
            total,
            parent: drugKey,
            drug,
          });
        });
        return row;
      });
    }

    return [];
  }, [
    GLPSColumn,
    drugsData,
    mapData,
    mapRegionData,
    organism,
    resistanceOptions,
    xAxisSelected,
    xAxisType,
    yAxisSelected,
    yAxisType,
    yAxisTypeTrend,
  ]);
  useEffect(() => {
    if (canGetData) {
      setPlotChart(() => {
        return (
          <>
            {configuredMapData?.map((item, index) => {
              return (
                <ResponsiveContainer
                  className={classes.graphContainer}
                  key={`bubble-graph-${index}`}
                  width={yAxisWidth + 65 * xAxisSelected.length}
                  height={index === 0 ? 65 + FIRST_ROW_AXIS_HEIGHT : 65}
                >
                  <ScatterChart cursor={isTouchDevice() ? 'default' : 'pointer'} margin={{ top: 0, bottom: 0 }}>
                    <XAxis
                      type="category"
                      dataKey="itemName"
                      interval={0}
                      height={index === 0 ? FIRST_ROW_AXIS_HEIGHT : 0}
                      tick={
                        index === 0
                          ? props => {
                              const title = getTitle(props.payload.value);

                              return (
                                <Tooltip title={title} placement="top">
                                  <text
                                    x={props.x}
                                    y={props.y}
                                    fontSize="14px"
                                    dy={-10}
                                    textAnchor="start"
                                    transform={`rotate(-45, ${props.x}, ${props.y})`}
                                    fill="rgb(128,128,128)"
                                  >
                                    {props.payload.value}
                                  </text>
                                </Tooltip>
                              );
                            }
                          : false
                      }
                      axisLine={false}
                      orientation="top"
                    />
                    <YAxis
                      type="number"
                      dataKey="index"
                      name={item?.name ? item.name.toLowerCase() : ''}
                      tick={false}
                      tickLine={false}
                      axisLine={false}
                      domain={[1, 1]}
                      label={{ value: formatYLabel(item?.name ?? ''), position: 'insideRight' }}
                      width={yAxisWidth}
                    />
                    <ZAxis type="number" dataKey="percentage" range={[3500, 3500]} />
                    <ChartTooltip
                      cursor={{ fill: hoverColor }}
                      wrapperStyle={{ zIndex: 100 }}
                      offset={40}
                      content={({ payload, active }) => {
                        if (payload !== null && active) {
                          const drug = payload[0]?.payload.drug ?? payload[0]?.payload.itemName;
                          const title = getTitle(drug);
                          const isRegion = payload[0]?.payload.count > 0;

                          return (
                            <div
                              className={classes.chartTooltipLabel}
                              style={{
                                marginTop: index + 1 === configuredMapData.length ? -40 : 0,
                              }}
                            >
                              <Typography variant="body1" fontWeight="500">
                                {payload[0]?.payload.typeName}
                              </Typography>
                              <Typography variant="caption" fontWeight="500">
                                Total: {payload[0]?.payload.total}
                              </Typography>
                              <Typography variant="body2">
                                {`${title}: ${payload[0]?.payload.count} (${payload[0]?.payload.percentage}%)`}
                              </Typography>
                              {isRegion &&
                                countriesTooltipForRegion(
                                  payload[0]?.payload.typeName,
                                  drug,
                                  payload[0]?.payload.parent,
                                )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter data={item.items} shape="square">
                      {item.items.map((option, index) => (
                        <Cell
                          name={option.drug}
                          key={`bubble-cell-${index}`}
                          fill={option.percentage === 0 ? darkGrey : mixColorScale(option.percentage)}
                        />
                      ))}
                      <LabelList
                        dataKey="percentage"
                        fontSize={12}
                        content={({ x, y, value }) => {
                          return (
                            <text
                              x={x + 33}
                              y={y + 38}
                              textAnchor="middle"
                              fontSize={15}
                              fontWeight={600}
                              fill={heatmapTextColor(value)}
                              pointerEvents="none"
                            >
                              {value}
                            </text>
                          );
                        }}
                      />
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              );
            })}
          </>
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configuredMapData,
    yAxisWidth,
    canGetData,
    classes,
    getTitle,
    hoverColor,
    mixColorScale,
    countriesTooltipForRegion,
  ]);

  return (
    <CardContent className={classes.bubbleGeographicGraph}>
      <div className={classes.graphWrapper}>
        {/* // BG is replaced from CVM for BubbleGeographicGraph and overflow visible for Download PDF */}
        <div className={classes.graph} style={loadingPDF ? { overflow: 'visible' } : undefined} id="BG">
          {plotChart}
        </div>
      </div>
      {(configuredMapData.length === 0 || yAxisSelected.length === 0) && (
        <Box className={classes.nothingSelected}>
          <Typography fontWeight={600}>{t('common.noDataToShow')}</Typography>
        </Box>
      )}
      <Divider className={classes.divider} />
      <div className={classes.bottomLegend}>
        <div className={classes.legend}>
          <Typography fontSize="0.75rem">0%</Typography>
          <Box className={classes.singleBox} sx={{ backgroundColor: darkGrey }} />
        </div>
        <div className={classes.legend}>
          <Typography fontSize="0.75rem">1%</Typography>
          <Box className={classes.gradientBox} style={{ backgroundImage: heatmapLegendGradient() }} />
          <Typography fontSize="0.75rem">100%</Typography>
        </div>
      </div>
      <PlottingOptionsPanel show={showFilter} className={classes.floatingFilter} anchorRef={filterButtonRef}>
          <Card elevation={3}>
            <CardContent>
              <div className={classes.titleWrapper}>
                <Typography variant="h6">{t('dashboard.filters.plotOptions.title')}</Typography>
                <Tooltip title={t('dashboard.filters.plotOptions.hideTooltip')} placement="top">
                  <IconButton onClick={() => setShowFilter(false)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <div className={classes.selectsWrapper}>
                <div className={classes.selectPreWrapper}>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">{t('common.selectRegionCountry')}</Typography>
                      <Tooltip title="Only show regions with N≥20 genomes." placement="top">
                        <InfoOutlined color="action" fontSize="small" className={classes.labelTooltipIcon} />
                      </Tooltip>
                    </div>
                    <Select
                      value={xAxisType}
                      onChange={handleChangeXAxisType}
                      inputProps={{ className: classes.selectInput }}
                      MenuProps={{ classes: { list: classes.selectMenu } }}
                      disabled={organism === 'none'}
                    >
                      <MenuItem value="country">Country</MenuItem>
                      <MenuItem value="region">Region</MenuItem>
                    </Select>
                  </div>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">{t('common.selectCountriesRegions')}</Typography>
                      <Tooltip title={t('common.navigateByLetter')} placement="top">
                        <InfoOutlined color="action" fontSize="small" className={classes.labelTooltipIcon} />
                      </Tooltip>
                    </div>
                    <Select
                      multiple
                      value={xAxisSelected}
                      onChange={event => handleChangeXAxisSelected({ event })}
                      displayEmpty
                      disabled={organism === 'none'}
                      endAdornment={
                        <Button
                          variant="outlined"
                          className={classes.selectButton}
                          onClick={() => handleChangeXAxisSelected({ all: true })}
                          disabled={organism === 'none'}
                          color={xAxisSelected.length === xAxisOptions.length ? 'error' : 'primary'}
                        >
                          {xAxisSelected.length === xAxisOptions.length ? t('common.clearAll') : t('common.selectAll')}
                        </Button>
                      }
                      inputProps={{ className: classes.multipleSelectInput }}
                      MenuProps={{
                        classes: { paper: classes.menuPaper, list: classes.selectMenu },
                      }}
                      renderValue={selected => <div>{t('common.selectedOfTotal', { selected: selected.length, total: xAxisOptions.length })}</div>}
                    >
                      {xAxisOptions.map((option, index) => (
                        <MenuItem key={`geo-x-axis-option-${index}`} value={option.name}>
                          <Checkbox checked={xAxisSelected.indexOf(option.name) > -1} />
                          <ListItemText primary={`${option.name} (total N=${option.count})`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className={classes.selectPreWrapper}>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">{t('common.columns')}</Typography>
                    </div>
                    <Select
                      value={yAxisType}
                      onChange={handleChangeYAxisType}
                      inputProps={{ className: classes.selectInput }}
                      MenuProps={{ classes: { list: classes.selectMenu } }}
                      disabled={organism === 'none'}
                    >
                      {yOptions
                        .filter(option => !option.organisms || option.organisms.includes(organism))
                        .map((option, index) => (
                          <MenuItem key={`y-col-option-${index}`} value={option.value}>
                            {option.labelKey ? t(option.labelKey) : option.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </div>
                  {yAxisType === 'determinant' && (
                    <div className={classes.selectWrapper}>
                      <div className={classes.labelWrapper}>
                        <Typography variant="caption">{t('common.selectDrugs')}</Typography>
                      </div>
                      <Select
                        value={yAxisTypeTrend}
                        onChange={handleChangeYAxisTypeTrend}
                        inputProps={{ className: classes.selectInput }}
                        MenuProps={{ classes: { list: classes.selectMenu } }}
                        disabled={organism === 'none'}
                      >
                        {/* Diagnostic: log trend options for this organism when determinant selected */}
                        {(trendOptionsMap[organism] || []).map((option, index) => (
                          <MenuItem key={`y-${organism}-option-${index}`} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  )}
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">Select {yAxisLabel}</Typography>
                      {['genotype', 'ngmast'].includes(yAxisType) && (
                        <Tooltip
                          title={`If the total ${yAxisLabel} are too many, only the first 20 options are shown at a time`}
                          placement="top"
                        >
                          <InfoOutlined color="action" fontSize="small" className={classes.labelTooltipIcon} />
                        </Tooltip>
                      )}
                    </div>
                    <Select
                      multiple
                      value={yAxisSelected}
                      onChange={event => handleChangeYAxisSelected({ event })}
                      displayEmpty
                      disabled={organism === 'none'}
                      endAdornment={
                        <Button
                          variant="outlined"
                          className={classes.selectButton}
                          onClick={() => handleChangeYAxisSelected({ all: true })}
                          disabled={organism === 'none'}
                          color={
                            yAxisSelected.length === filteredYAxisOptions.length ||
                            yAxisSelected.some(x => !yAxisOptions.slice(0, 20).includes(x))
                              ? 'error'
                              : 'primary'
                          }
                        >
                          {yAxisSelected.length === filteredYAxisOptions.length ||
                          yAxisSelected.some(x => !yAxisOptions.slice(0, 20).includes(x))
                            ? t('common.clearAll')
                            : t('common.select20')}
                        </Button>
                      }
                      inputProps={{ className: classes.multipleSelectInput }}
                      MenuProps={{
                        disableAutoFocusItem: true,
                        classes: { paper: classes.menuPaper, list: classes.selectMenu },
                      }}
                      renderValue={selected => (
                        <div>
                          {Array.isArray(selected) && selected.length > 0
                            ? t('common.selectedOfTotal', { selected: selected.length, total: yAxisOptions.length })
                            : 'No options selected'}
                        </div>
                      )}
                      onClose={clearSearch}
                    >
                      <Box
                        className={classes.selectSearch}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <TextField
                          variant="standard"
                          placeholder={
                            organismHasLotsOfGenotypes && ['genotype', 'ngmast'].includes(yAxisType)
                              ? 'Search for more...'
                              : 'Search...'
                          }
                          fullWidth
                          value={genotypeSearch}
                          onChange={handleChangeSearch}
                          InputProps={
                            genotypeSearch === ''
                              ? undefined
                              : {
                                  endAdornment: (
                                    <IconButton size="small" onClick={clearSearch}>
                                      <Clear />
                                    </IconButton>
                                  ),
                                }
                          }
                        />
                      </Box>
                      {filteredYAxisOptions.map((option, index) => (
                        <MenuItem key={`geo-y-axis-option-${index}`} value={option}>
                          <Checkbox checked={yAxisSelected.indexOf(option) > -1} />
                          <ListItemText primary={getOptionLabel(option)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
      </PlottingOptionsPanel>
    </CardContent>
  );
};
